import React, { useState, useRef, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useMentions } from '@/hooks/useMentions';
import { ROLE_LABELS } from '@/constants/roles';

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  placeholder,
  className,
  disabled
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { mentionables } = useMentions();

  const toDisplayValue = (text: string) => text.replace(/@\[([^\]]+)\]\([^:]+:[^)]+\)/g, '@$1');
  const toStoredValue = (displayText: string) => {
    let storedText = displayText;
    const mentionRegex = /@\[([^\]]+)\]\(([^:]+):([^)]+)\)/g;
    let match;
    while ((match = mentionRegex.exec(value)) !== null) {
      storedText = storedText.replace(`@${match[1]}`, match[0]);
    }
    return storedText;
  };
  const toStoredCursorPosition = (displayText: string, displayCursor: number) => {
    const displayBeforeCursor = displayText.substring(0, displayCursor);
    let storedCursor = displayCursor;
    const mentionRegex = /@\[([^\]]+)\]\(([^:]+):([^)]+)\)/g;
    let match;
    while ((match = mentionRegex.exec(value)) !== null) {
      const storedMention = match[0];
      const displayMention = `@${match[1]}`;
      const displayIndex = toDisplayValue(value.substring(0, match.index)).length;
      if (displayIndex + displayMention.length <= displayBeforeCursor.length) {
        storedCursor += storedMention.length - displayMention.length;
      }
    }
    return storedCursor;
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = toStoredValue(e.target.value);
    const newCursorPosition = toStoredCursorPosition(e.target.value, e.target.selectionStart);
    
    onChange(newValue);
    setCursorPosition(newCursorPosition);

    // Check if we're typing an @ mention
    const textBeforeCursor = newValue.substring(0, newCursorPosition);
    const mentionMatch = textBeforeCursor.match(/@([^@\n\r]*)$/);

    if (mentionMatch) {
      setSearchTerm(mentionMatch[1]);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const insertMention = (mention: { id: string; name: string; type: 'user' | 'client'; role?: string }) => {
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@([^@\n\r]*)$/);

    if (mentionMatch) {
      const beforeMention = textBeforeCursor.substring(0, textBeforeCursor.length - mentionMatch[0].length);
      const mentionText = `@[${mention.name}](${mention.type}:${mention.id}) `;
      const newValue = beforeMention + mentionText + textAfterCursor;
      
      onChange(newValue);
      setShowSuggestions(false);

      // Focus back on textarea
      setTimeout(() => {
        if (textareaRef.current) {
          const newPosition = beforeMention.length + mentionText.length;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newPosition, newPosition);
        }
      }, 0);
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredSuggestions = mentionables
    .filter(m => !normalizedSearch || m.name.toLowerCase().includes(normalizedSearch))
    .sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const aStarts = normalizedSearch && aName.startsWith(normalizedSearch);
      const bStarts = normalizedSearch && bName.startsWith(normalizedSearch);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return a.name.localeCompare(b.name);
    })
    .slice(0, 8);

  const staffSuggestions = filteredSuggestions.filter(m => m.type === 'user');
  const clientSuggestions = filteredSuggestions.filter(m => m.type === 'client');

  // Display formatted text (convert mentions to readable format)
  const displayValue = toDisplayValue(value);

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={displayValue}
        onChange={handleTextChange}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
      />
      
      {showSuggestions && filteredSuggestions.length > 0 && (
        <Card className="absolute z-[100] mt-1 max-h-56 overflow-y-auto w-full shadow-lg border bg-popover text-popover-foreground">
          <div className="p-1">
            {staffSuggestions.length > 0 && (
              <>
                <div className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Staff</div>
                {staffSuggestions.map((mention) => (
                  <button
                    key={`${mention.type}-${mention.id}`}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded hover:bg-accent transition-colors",
                      "flex items-center justify-between"
                    )}
                    onClick={() => insertMention(mention)}
                    type="button"
                  >
                    <span className="font-medium">{mention.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {ROLE_LABELS[mention.role as keyof typeof ROLE_LABELS] || 'Staff'}
                    </span>
                  </button>
                ))}
              </>
            )}
            {clientSuggestions.length > 0 && (
              <>
                <div className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Clients</div>
                {clientSuggestions.map((mention) => (
                  <button
                    key={`${mention.type}-${mention.id}`}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded hover:bg-accent transition-colors",
                      "flex items-center justify-between"
                    )}
                    onClick={() => insertMention(mention)}
                    type="button"
                  >
                    <span className="font-medium">{mention.name}</span>
                    <span className="text-xs text-muted-foreground">Client</span>
                  </button>
                ))}
              </>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};
